import {
  Clear as ClearIcon,
  Download as DownloadIcon,
  ManageSearch as ShopperDetailsIcon,
} from "@mui/icons-material";
import { Alert, Box, Button, GridLegacy as Grid } from "@mui/material";
import { subDays } from "date-fns";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { AdminDatePicker, AdminFilterPanel, PageShell } from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { useAuth } from "@/contexts/AuthContext";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import { AnalyticsServiceV2, ReportExport } from "@/services/features/AnalyticsServiceV2";
import { Store, StoreService } from "@/services/features/StoreService";
import { formatInteger } from "@/utils/number";
import { formatStoreSelectorLabel } from "@/utils/store";
import { formatStoreDateKey, getBrowserTimezone } from "@/utils/storeTime";

const DEFAULT_END_DATE = new Date();
const DEFAULT_START_DATE = subDays(DEFAULT_END_DATE, 6);
const EXPORT_POLL_INTERVAL_MS = 2_000;
const MAX_EXPORT_POLLS = 900;

const toDateParam = (value: Date) => formatStoreDateKey(value, getBrowserTimezone());

const parseIdList = (raw: string | null): number[] => {
  if (!raw) {
    return [];
  }

  return raw
    .split(",")
    .map((entry) => Number(entry.trim()))
    .filter((entry) => Number.isFinite(entry));
};

const parseDateParam = (raw: string | null, fallback: Date): Date => {
  if (!raw) {
    return fallback;
  }

  const parsed = new Date(`${raw}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

const safeText = (value: string | null | undefined, fallback = ""): string => {
  if (typeof value !== "string") {
    return fallback;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const sortCustomers = (customers: Customer[]) =>
  [...customers].sort((left, right) =>
    safeText(left.name, `Customer ${left.id}`).localeCompare(
      safeText(right.name, `Customer ${right.id}`),
    ),
  );

const sortStores = (stores: Store[]) =>
  [...stores].sort((left, right) => {
    const customerCompare = safeText(
      left.customerName,
      `Customer ${left.customerId}`,
    ).localeCompare(safeText(right.customerName, `Customer ${right.customerId}`));
    if (customerCompare !== 0) {
      return customerCompare;
    }
    return safeText(left.name, `Store ${left.id}`).localeCompare(
      safeText(right.name, `Store ${right.id}`),
    );
  });

const buildFilename = (stores: Store[], startDate: Date, endDate: Date) => {
  const storePart = stores.length === 1 ? `store-${stores[0].id}` : `${stores.length}-stores`;
  return `shopper-conversions_${storePart}_${toDateParam(startDate)}_to_${toDateParam(endDate)}.jsonl`;
};

const downloadBlobFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });

const ShopperDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [initialSearchParams] = React.useState(() => new URLSearchParams(searchParams));

  const analyticsService = React.useMemo(() => AnalyticsServiceV2.getInstance(), []);
  const customerService = React.useMemo(() => CustomerService.getInstance(), []);
  const storeService = React.useMemo(() => StoreService.getInstance(), []);

  const [customers, setCustomers] = React.useState<Customer[]>([]);
  const [stores, setStores] = React.useState<Store[]>([]);
  const [selectedCustomers, setSelectedCustomers] = React.useState<Customer[]>([]);
  const [selectedStores, setSelectedStores] = React.useState<Store[]>([]);
  const [startDate, setStartDate] = React.useState<Date | null>(DEFAULT_START_DATE);
  const [endDate, setEndDate] = React.useState<Date | null>(DEFAULT_END_DATE);
  const [isLoadingCustomers, setIsLoadingCustomers] = React.useState(true);
  const [isLoadingStores, setIsLoadingStores] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [initialized, setInitialized] = React.useState(false);
  const [currentExport, setCurrentExport] = React.useState<ReportExport | null>(null);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    document.title = `${t("layout.sidebar.craveAdminSection.fields.shopperDetailsExport")} - Crave Admin`;
  }, [t]);

  const filteredStores = React.useMemo(() => {
    if (selectedCustomers.length === 0) {
      return stores;
    }

    const selectedCustomerIds = new Set(selectedCustomers.map((customer) => customer.id));
    return stores.filter((store) => selectedCustomerIds.has(store.customerId));
  }, [selectedCustomers, stores]);

  const syncSearchParams = React.useCallback(
    (
      nextCustomers: Customer[],
      nextStores: Store[],
      nextStartDate: Date | null,
      nextEndDate: Date | null,
    ) => {
      const params = new URLSearchParams();

      if (nextCustomers.length > 0) {
        params.set("customerIds", nextCustomers.map((customer) => customer.id).join(","));
      }

      if (nextStores.length > 0) {
        params.set("storeIds", nextStores.map((store) => store.id).join(","));
      }

      if (nextStartDate) {
        params.set("startDate", toDateParam(nextStartDate));
      }

      if (nextEndDate) {
        params.set("endDate", toDateParam(nextEndDate));
      }

      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  React.useEffect(() => {
    let cancelled = false;

    const loadFilters = async () => {
      setIsLoadingCustomers(true);
      setIsLoadingStores(false);

      try {
        const customerResponse = await customerService.getAllCustomers();
        if (cancelled) {
          return;
        }

        setIsLoadingCustomers(false);
        setIsLoadingStores(true);
        const storeResponse = await storeService.getStores();
        if (cancelled) {
          return;
        }

        const nextCustomers = sortCustomers(customerResponse.data ?? []);
        const nextStores = sortStores(storeResponse.data ?? []);
        const initialCustomerIds = new Set(parseIdList(initialSearchParams.get("customerIds")));
        const initialStoreIds = new Set(parseIdList(initialSearchParams.get("storeIds")));
        const nextStartDate = parseDateParam(
          initialSearchParams.get("startDate"),
          DEFAULT_START_DATE,
        );
        const nextEndDate = parseDateParam(initialSearchParams.get("endDate"), DEFAULT_END_DATE);

        const initialSelectedCustomers = nextCustomers.filter((customer) =>
          initialCustomerIds.has(customer.id),
        );

        const initialSelectedStores = nextStores.filter((store) => {
          if (!initialStoreIds.has(store.id)) {
            return false;
          }

          if (initialSelectedCustomers.length === 0) {
            return true;
          }

          return initialSelectedCustomers.some((customer) => customer.id === store.customerId);
        });

        setCustomers(nextCustomers);
        setStores(nextStores);
        setSelectedCustomers(initialSelectedCustomers);
        setSelectedStores(initialSelectedStores);
        setStartDate(nextStartDate);
        setEndDate(nextEndDate);
        setInitialized(true);
      } catch (error) {
        if (cancelled) {
          return;
        }
        const message =
          error instanceof Error ? error.message : t("operations.shopperExport.errors.loadFilters");
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        if (!cancelled) {
          setIsLoadingCustomers(false);
          setIsLoadingStores(false);
        }
      }
    };

    void loadFilters();

    return () => {
      cancelled = true;
    };
  }, [customerService, enqueueSnackbar, initialSearchParams, storeService, t]);

  React.useEffect(() => {
    if (!initialized) {
      return;
    }

    syncSearchParams(selectedCustomers, selectedStores, startDate, endDate);
  }, [endDate, initialized, selectedCustomers, selectedStores, startDate, syncSearchParams]);

  const handleCustomerChange = React.useCallback((nextCustomers: Customer[]) => {
    const allowedCustomerIds = new Set(nextCustomers.map((customer) => customer.id));

    setSelectedCustomers(nextCustomers);
    setSelectedStores((previousStores) => {
      if (allowedCustomerIds.size === 0) {
        return previousStores;
      }

      return previousStores.filter((store) => allowedCustomerIds.has(store.customerId));
    });
  }, []);

  const handleReset = React.useCallback(() => {
    setSelectedCustomers([]);
    setSelectedStores([]);
    setStartDate(DEFAULT_START_DATE);
    setEndDate(DEFAULT_END_DATE);
  }, []);

  const handleDownload = React.useCallback(async () => {
    if (selectedStores.length === 0) {
      enqueueSnackbar(t("operations.shopperExport.validation.storeRequired"), {
        variant: "warning",
      });
      return;
    }

    if (!startDate || !endDate) {
      enqueueSnackbar(t("operations.shopperExport.validation.datesRequired"), {
        variant: "warning",
      });
      return;
    }

    if (startDate > endDate) {
      enqueueSnackbar(t("operations.shopperExport.validation.dateOrder"), {
        variant: "warning",
      });
      return;
    }

    setDownloading(true);
    setCurrentExport(null);

    try {
      const response = await analyticsService.createConversionExport({
        storeIds: selectedStores.map((store) => store.id),
        startDate: toDateParam(startDate),
        endDate: toDateParam(endDate),
      });
      let reportExport = response.data;
      if (!reportExport) {
        throw new Error(t("operations.shopperExport.errors.create"));
      }
      setCurrentExport(reportExport);

      for (let pollCount = 0; pollCount < MAX_EXPORT_POLLS; pollCount += 1) {
        if (reportExport.status === "COMPLETE") {
          const file = await analyticsService.downloadReportExport(reportExport.id);
          if (!mountedRef.current) {
            return;
          }
          downloadBlobFile(file, buildFilename(selectedStores, startDate, endDate));
          enqueueSnackbar(
            t("operations.shopperExport.messages.downloaded", {
              formattedCount: formatInteger(
                reportExport.rowCount ?? 0,
                i18n.resolvedLanguage || i18n.language,
              ),
            }),
            { variant: "success" },
          );
          return;
        }

        if (reportExport.status === "FAILED") {
          throw new Error(
            reportExport.errorMessage || t("operations.shopperExport.errors.generation"),
          );
        }
        if (reportExport.status === "EXPIRED") {
          throw new Error(t("operations.shopperExport.errors.expired"));
        }

        await wait(EXPORT_POLL_INTERVAL_MS);
        if (!mountedRef.current) {
          return;
        }
        const statusResponse = await analyticsService.getReportExport(reportExport.id);
        if (!statusResponse.data) {
          throw new Error(t("operations.shopperExport.errors.status"));
        }
        reportExport = statusResponse.data;
        setCurrentExport(reportExport);
      }

      throw new Error(t("operations.shopperExport.errors.timeout"));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("operations.shopperExport.errors.generation");
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      if (mountedRef.current) {
        setDownloading(false);
      }
    }
  }, [
    analyticsService,
    endDate,
    enqueueSnackbar,
    i18n.language,
    i18n.resolvedLanguage,
    selectedStores,
    startDate,
    t,
  ]);

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("layout.sidebar.craveAdminSection.fields.shopperDetailsExport")}
      icon={<ShopperDetailsIcon />}
      titleAccessory={
        <LoadingRing loading={isLoadingCustomers || isLoadingStores || downloading} />
      }
    >
      <Box sx={{ p: 0 }}>
        <AdminFilterPanel
          sx={{ mb: 3 }}
          hint={<Alert severity="info">{t("operations.shopperExport.fileInfo")}</Alert>}
          primaryAction={{
            label: downloading
              ? t("operations.shopperExport.actions.preparing")
              : t("operations.shopperExport.actions.submit"),
            icon: <DownloadIcon />,
            onClick: () => void handleDownload(),
            disabled: downloading,
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md>
              <MuiAutocompleteMultiSelect
                label={t("operations.shopperExport.fields.customers")}
                options={customers}
                value={selectedCustomers}
                onChange={handleCustomerChange}
                multiSelect
                getOptionLabel={(option) =>
                  safeText(
                    option.name,
                    t("operations.shopperExport.fallback.customer", { id: option.id }),
                  )
                }
                getOptionKey={(option) => option.id}
                loading={isLoadingCustomers}
                disabled={isLoadingCustomers}
              />
            </Grid>
            <Grid item xs={12} md>
              <MuiAutocompleteMultiSelect
                label={t("operations.shopperExport.fields.stores")}
                options={filteredStores}
                value={selectedStores}
                onChange={setSelectedStores}
                multiSelect
                rowHeight="default"
                getOptionLabel={(option) =>
                  formatStoreSelectorLabel(
                    {
                      ...option,
                      name: safeText(
                        option.name,
                        t("operations.shopperExport.fallback.store", { id: option.id }),
                      ),
                    },
                    t,
                    user?.role,
                    "name",
                  )
                }
                getOptionKey={(option) => option.id}
                loading={isLoadingStores}
                disabled={isLoadingCustomers || isLoadingStores}
                getOptionSecondary={(option) =>
                  t("operations.shopperExport.fields.storeOption", {
                    customer: safeText(
                      option.customerName,
                      t("operations.shopperExport.fallback.customer", {
                        id: option.customerId,
                      }),
                    ),
                    id: option.id,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <AdminDatePicker
                label={t("operations.shopperExport.fields.startDate")}
                value={startDate}
                onChange={setStartDate}
              />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <AdminDatePicker
                label={t("operations.shopperExport.fields.endDate")}
                value={endDate}
                onChange={setEndDate}
              />
            </Grid>
            <Grid item xs={12} md="auto" sx={{ alignSelf: "center" }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleReset}
                disabled={downloading}
                color="primary"
                size="medium"
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                {t("common.clearFilters")}
              </Button>
            </Grid>
          </Grid>
        </AdminFilterPanel>

        {currentExport && (
          <Alert
            severity={
              currentExport.status === "FAILED"
                ? "error"
                : currentExport.status === "COMPLETE"
                  ? "success"
                  : "info"
            }
            sx={{ mb: 2 }}
          >
            {t("operations.shopperExport.status", {
              id: currentExport.id,
              status: t(`operations.shopperExport.statuses.${currentExport.status}`),
            })}
          </Alert>
        )}
      </Box>
    </PageShell>
  );
};

export default ShopperDetailsPage;
